/**
 * Small, always-honest badge telling the technician whether live location
 * tracking is actually running for this inspection.
 *
 * Why it exists: tracking is deliberately PASSIVE (the app never raises its own
 * geolocation prompt, because a denied prompt poisons the permission for the
 * whole origin and breaks Set-GPS too). The cost of that is silence — when
 * permission was never granted, tracking simply never starts and says nothing.
 * One technician went 7 of 12 working days with no GPS trail before anyone
 * noticed. Per-photo map pins depend on the same permission, so a quiet device
 * also produces photos with no pin.
 *
 * Design constraints — this sits on a screen a tech uses all day:
 *  - It must not nag, block, or shout when everything is fine (healthy state is
 *    a muted chip, same weight as the rest of the header).
 *  - It must be honest about the middle state: "allowed, but no fix yet" is NOT
 *    tracking, and pretending otherwise is what made the original problem
 *    invisible. The freshness check means a watch that silently dies (device
 *    suspended it, OS revoked location) degrades to `acquiring` instead of
 *    claiming "on" forever.
 *  - Reading the state is side-effect free, so polling it can never trigger an
 *    OS prompt. The ONLY path to the browser prompt is the tech tapping this
 *    badge, which opens an explained modal first (`onExplainTap`).
 */
import { useEffect, useState } from 'react';
import { MapPin, MapPinOff, LocateFixed } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { checkGeolocationPermission } from '../lib/locationHeartbeat';
import {
  readLocationTrackingState,
  type LocationTrackingState,
} from '../lib/locationState';

/**
 * Re-evaluate the (purely local) tracking state on this cadence. Well under the
 * 3-minute staleness threshold, and cheap: a Permissions API read plus two
 * in-memory lookups, no network and no geolocation call.
 */
const POLL_MS = 10_000;

interface LocationTrackingIndicatorProps {
  /**
   * The workflow already knows location is hard-blocked (a heartbeat reported
   * permission_denied, or a request came back denied). Sticky on iOS, where the
   * permission API reports 'unknown' rather than 'denied'.
   */
  blocked?: boolean;
  /**
   * Called when the tech taps the badge in its `not_granted` state. MUST open
   * the explained prompt — never call requestLocationPermission() directly from
   * here, and never on mount.
   */
  onExplainTap?: () => void;
  /** False disables the tap (kill-switch off) — the badge still reports status. */
  canRequest?: boolean;
  /** A permission request is in flight. */
  busy?: boolean;
  className?: string;
}

export function LocationTrackingIndicator({
  blocked = false,
  onExplainTap,
  canRequest = true,
  busy = false,
  className = '',
}: LocationTrackingIndicatorProps) {
  const { t } = useTranslation('inspection');
  const [state, setState] = useState<LocationTrackingState | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      // checkGeolocationPermission only queries the Permissions API — it never
      // calls getCurrentPosition, so this cannot raise a prompt.
      const permission = await checkGeolocationPermission();
      if (cancelled) return;
      const next = readLocationTrackingState(permission, { blocked });
      // Bail out of the re-render when nothing changed; this polls for the whole
      // life of a long inspection form.
      setState((prev) => (prev === next ? prev : next));
    };
    void tick();
    const id = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [blocked]);

  if (state === null) return null; // not determined yet — say nothing

  const base =
    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none whitespace-nowrap';
  const quiet = 'border-line bg-subtle text-theme-secondary';
  const warn =
    'border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300';

  if (state === 'tracking') {
    return (
      <span
        className={`${base} ${quiet} ${className}`}
        title={t('locationStatus.trackingHint', {
          defaultValue: 'Your location is being recorded for this inspection.',
        })}
      >
        <MapPin size={11} className="text-emerald-600 dark:text-emerald-400" />
        {t('locationStatus.tracking', { defaultValue: 'Location on' })}
      </span>
    );
  }

  if (state === 'acquiring') {
    return (
      <span
        className={`${base} ${quiet} ${className}`}
        title={t('locationStatus.acquiringHint', {
          defaultValue:
            'Location is allowed but your device has not reported a position yet. Step outside or wait a moment.',
        })}
      >
        <LocateFixed size={11} className="text-amber-600 dark:text-amber-400" />
        {t('locationStatus.acquiring', { defaultValue: 'Finding location...' })}
      </span>
    );
  }

  if (state === 'blocked') {
    return (
      <span
        className={`${base} ${warn} ${className}`}
        title={t('locationStatus.blockedHint', {
          defaultValue:
            'Location is blocked for this app, so nothing is being recorded. It has to be re-enabled in your device settings.',
        })}
      >
        <MapPinOff size={11} />
        {t('locationStatus.blocked', { defaultValue: 'Location blocked' })}
      </span>
    );
  }

  // not_granted — the one state a tap can actually fix.
  if (!canRequest || !onExplainTap) {
    return (
      <span className={`${base} ${warn} ${className}`}>
        <MapPinOff size={11} />
        {t('locationStatus.off', { defaultValue: 'Location off' })}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onExplainTap}
      disabled={busy}
      className={`${base} ${warn} disabled:opacity-50 ${className}`}
      title={t('locationStatus.offHint', {
        defaultValue: 'Nothing is being recorded for this inspection. Tap to turn location on.',
      })}
    >
      <MapPinOff size={11} />
      {t('locationStatus.off', { defaultValue: 'Location off' })}
      <span className="underline">{t('locationStatus.turnOn', { defaultValue: 'Turn on' })}</span>
    </button>
  );
}
